import { SxProps } from "@mui/material";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { MultipleUserPickerQuery } from "generated-graphql/graphql";
import React, { forwardRef, useCallback, useMemo } from "react";
import { ControllerFieldState } from "react-hook-form";

import { PickerInput, useMultiPickerWithHydration } from "../hooks/useMultiPickerWithHydration";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";

const USERS_QUERY = gql(`
  query MultipleUserPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        email
        person {
          first
          last
        }
      }
      count
    }
  }
`);

/** Same selection as {@link USERS_QUERY}, but backed by `staffUsers` (no tenant context). */
const STAFF_USERS_QUERY = gql(`
  query MultipleUserPickerStaff($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users: staffUsers(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        email
        person {
          first
          last
        }
      }
      count
    }
  }
`);

/**
 * Cross-tenant user search for staff tooling. Backed by `staffSearchAnyUser`,
 * which has no tenant scope and no user-type restriction — it returns Encamp
 * customers + staff alike. Required by /staff routes that need to look up
 * arbitrary users (e.g. the Scout conversation observer's user filter).
 */
const STAFF_SEARCH_ANY_USER_QUERY = gql(`
  query MultipleUserPickerStaffAny($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users: staffSearchAnyUser(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        email
        person {
          first
          last
        }
      }
      count
    }
  }
`);

export type UserOption = {
  id: string;
  email: string;
  name: string;
};

interface MultipleUserPickerProps extends Partial<ControllerFieldState> {
  onChange: (users: UserOption[] | null) => void;
  /**
   * When true, search Encamp staff via `staffUsers` (aliased as `users` in the document) and
   * skip the tenant `users` query entirely. Required on `/staff` routes, where the tenant
   * `users` query has no `currentTenantId` to scope by. Mirrors the staff-only branch in
   * {@link ReviewerSignerPicker} and `BulkAddReviewersDialog`.
   */
  staffOnly?: boolean;
  /**
   * When true, search every user across every tenant via `staffSearchAnyUser`.
   * `staffOnly` is restricted to Encamp employees; this variant is for staff
   * tooling that needs to find customer users without a current-tenant
   * context (e.g. the Scout conversation observer's user filter). Requires
   * an `isStaff()` caller — the resolver enforces it server-side.
   * Takes precedence over `staffOnly` when both are set.
   */
  staffSearchAnyUser?: boolean;
  /** When false, single-select (first selected user only). Default true. */
  multiple?: boolean;
  defaultSearchTerm?: string;
  disabledUsersById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?: (UserOption | PickerInput<UserOption> | string)[] | null;
  lazyLoad?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  showErrors?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export const MultipleUserPicker: React.FC<MultipleUserPickerProps> = forwardRef<
  HTMLDivElement,
  MultipleUserPickerProps
>(function MultipleUserPicker(
  {
    onChange,
    multiple = true,
    defaultSearchTerm = "",
    disabledUsersById = [],
    disabledOptionLabel = "",
    label = "User",
    value,
    error,
    disabled,
    showErrors = false,
    sx,
    onOpen,
    onClose,
    staffOnly = false,
    staffSearchAnyUser = false,
  },
  ref
) {
  const isMultiple = multiple;
  // staffSearchAnyUser takes precedence so the picker can be dropped into a
  // staff-tooling context without callers also having to clear staffOnly.
  const activeQuery = staffSearchAnyUser
    ? STAFF_SEARCH_ANY_USER_QUERY
    : staffOnly
      ? STAFF_USERS_QUERY
      : USERS_QUERY;

  const getItems = useCallback((data: MultipleUserPickerQuery) => {
    return (
      data.users?.items.map((user) => ({
        id: user.id,
        email: user.email,
        name: user.person ? getFullName(user.person) : user.email,
      })) ?? []
    );
  }, []);

  const normalizedValue = useMemo(() => {
    return value?.map((v) => {
      if (typeof v === "string") {
        return { id: v, email: v };
      }
      return v;
    });
  }, [value]);

  const { resolvedValue, isHydrating, enhancedOnChange, enhancedGetItems } =
    useMultiPickerWithHydration<UserOption, MultipleUserPickerQuery>({
      value: normalizedValue || null,
      query: activeQuery as typeof USERS_QUERY,
      getItems,
      cacheKey: "email",
      idsSearchPrefix: "email",
      onChange,
    });

  const getOptionLabel = useCallback((user: UserOption) => {
    if (!user) return "";
    return user.name !== user.email ? `${user.name} (${user.email})` : user.email;
  }, []);

  const isOptionEqualToValue = useCallback(
    (option: UserOption, value: UserOption) => option?.email === value?.email,
    []
  );

  return (
    <>
      <AsyncAutocomplete
        ref={ref}
        query={activeQuery}
        initialPageSize={20}
        initialSortModel={staffOnly ? [{ field: "email", sort: "asc" }] : undefined}
        variables={{}}
        fullWidth
        textFieldProps={{ size: "small", variant: "outlined" }}
        getItems={enhancedGetItems}
        getOptionLabel={getOptionLabel}
        placeholderLabel={label}
        onChange={enhancedOnChange}
        value={
          (isMultiple ? resolvedValue : (resolvedValue[0] ?? null)) as
            | UserOption[]
            | UserOption
            | null
        }
        error={error}
        // This component renders its own ErrorDisplay below (gated on showErrors), so
        // suppress AsyncAutocomplete's built-in one — otherwise it reserves a fixed
        // helper-text gap under the field (and double-renders errors when showErrors).
        hideErrors={!showErrors}
        disabled={disabled || isHydrating}
        additionalSearchString={defaultSearchTerm}
        multiple={isMultiple}
        filterSelectedOptions={isMultiple}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionDisabled={(option: UserOption) => disabledUsersById.includes(option?.id ?? "")}
        loading={isHydrating}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {getOptionLabel(option)}
            {disabledUsersById.includes(option?.id ?? "") && (
              <span>&nbsp;({disabledOptionLabel})</span>
            )}
          </li>
        )}
        sx={sx}
        onOpen={onOpen}
        onClose={onClose}
        // Cap the option list to ~6 rows (each ~48px) so a long staff roster scrolls
        // instead of growing tall enough to flip above the input.
        listboxProps={{ style: { maxHeight: 300 } }}
      />
      {showErrors && <ErrorDisplay error={error} />}
    </>
  );
});
